import { useState, useEffect, useCallback } from 'react'
import newsService from '../services/newsService'

/**
 * Custom hook for news functionality
 * Handles fetching developer and tech news from Hacker News
 */
export const useNews = () => {
  const [news, setNews] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selectedCategory, setSelectedCategory] = useState('all')

  // Load news articles
  const loadNews = useCallback(async (type = 'top', limit = 20) => {
    setLoading(true)
    setError(null)

    try {
      console.log(`📰 Loading news: ${type}`)
      const articles = await newsService.getNews(type, limit)
      setNews(articles)
      console.log(`📰 Loaded ${articles.length} news articles`)
    } catch (err) {
      console.error('Error loading news:', err)
      setError(err.message)
      // Set demo data as fallback
      setNews(newsService.getDemoNews())
    } finally {
      setLoading(false)
    }
  }, [])

  // Load news when component mounts
  useEffect(() => {
    loadNews()
  }, [loadNews])

  // Filter news by category
  const filteredNews = news.filter(article => {
    if (selectedCategory === 'all') return true
    return article.category === selectedCategory
  })

  // Get available categories
  const categories = ['all', ...new Set(news.map(article => article.category))]

  // Refresh news (bypass cache)
  const refresh = useCallback(() => {
    newsService.clearCache()
    loadNews()
  }, [loadNews])

  return {
    // State
    news: filteredNews,
    allNews: news,
    loading,
    error,
    categories,
    selectedCategory,
    
    // Actions
    setSelectedCategory,
    refresh,
    loadNews
  }
}

/**
 * Hook specifically for news widget data
 */
export const useNewsWidget = () => {
  const [widgetNews, setWidgetNews] = useState([])
  const [loading, setLoading] = useState(false)

  const loadWidgetNews = useCallback(async () => {
    setLoading(true)
    try {
      const news = await newsService.getWidgetNews()
      setWidgetNews(news)
    } catch (error) {
      console.error('Error loading news widget:', error)
      setWidgetNews(newsService.getDemoWidgetNews())
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadWidgetNews()
  }, [loadWidgetNews])

  return {
    widgetNews,
    loading,
    refresh: loadWidgetNews
  }
}

export default useNews
